import React from 'react';
import { Box, Flex, HStack, Text, IconButton, VStack, Tag } from '@chakra-ui/react';
import { Handle, Position } from 'reactflow';
import { FiEdit, FiCopy, FiTrash2, FiList } from 'react-icons/fi';

const STEP_TYPE_MAP = {
    callFlowNode:         { label: 'Call xflow',    color: 'teal'  },
    callWhatsappFlowNode: { label: 'WhatsApp Flow',  color: 'green' },
    ivrNode:              { label: 'IVR Call',       color: 'cyan'  },
};

const MultiStepNode = ({ data }) => {
    const stepCount = data.stepCount || 3;
    const stepNames = data.stepNames || Array.from({ length: stepCount }, (_, i) => `Step ${i + 1}`);
    const stepTypes = data.stepTypes || [];

    return (
        <Box
            bg="white"
            borderWidth="2px"
            borderColor="#9333ea"
            borderRadius="xl"
            boxShadow={data.selected ? 'xl' : 'lg'}
            width="280px"
        >
            <Handle type="target" position={Position.Left} style={{ background: 'black' }} />

            <Flex
                bgGradient="linear(to-r, #9333ea, #7e22ce)"
                color="white"
                px={4}
                py={2.5}
                borderTopRadius="xl"
                align="center"
                justify="space-between"
            >
                <HStack spacing={2}>
                    <FiList />
                    <Text noOfLines={1} fontSize="sm" fontWeight="medium">Multi Step</Text>
                </HStack>
                <HStack spacing={1}>
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiEdit />}
                        onClick={(e) => { e.stopPropagation(); data.onEdit?.(data.id); }}
                    />
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiCopy />}
                        onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}
                    />
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiTrash2 />}
                        onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
                    />
                </HStack>
            </Flex>

            <Box p={3.5}>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                    {data.label || 'Multi Step Flow'}
                </Text>
                <Text fontSize="xs" color="gray.500" mb={3}>
                    {stepCount} steps configured
                </Text>

                {/* Neither this list nor a row can clip overflow: each row's Handle is
                    deliberately positioned outside its own bounds (see .branch-handle in
                    flowHandles.css) to reach the node's true edge, and `overflow` on any
                    ancestor — including overflowY alone, which forces overflowX to
                    "auto" too per the CSS spec — would clip it invisible. So this list
                    grows with step count instead of scrolling, same as every other
                    node's branch list (Smart Condition, DTMF, Customer Profile, ...). */}
                <VStack spacing={2} align="stretch">
                    {Array.from({ length: stepCount }, (_, i) => (
                        <Box
                            key={i}
                            position="relative"
                            bg="purple.50"
                            border="1px"
                            borderColor="purple.200"
                            borderRadius="lg"
                            pl={2.5}
                            pr={4}
                            py={1.5}
                        >
                            <HStack spacing={2}>
                                <Box w="8px" h="8px" borderRadius="full" bg="purple.500" flexShrink={0} />
                                <Text fontSize="xs" fontWeight="medium" color="purple.700" noOfLines={1} flex={1}>
                                    {stepNames[i] || `Step ${i + 1}`}
                                </Text>
                                {stepTypes[i] && STEP_TYPE_MAP[stepTypes[i]] && (
                                    <Tag
                                        colorScheme={STEP_TYPE_MAP[stepTypes[i]].color}
                                        variant="subtle"
                                        fontSize="9px"
                                        px={1}
                                        flexShrink={0}
                                    >
                                        {STEP_TYPE_MAP[stepTypes[i]].label}
                                    </Tag>
                                )}
                            </HStack>
                            <Handle
                                type="source"
                                position={Position.Right}
                                id={`step-${i}`}
                                className="branch-handle"
                                style={{
                                    background: '#9333ea',
                                    width: '10px',
                                    height: '10px',
                                    // See flowHandles.css .branch-handle: this row's own
                                    // 1px border + the node body's p={3.5}=14px padding +
                                    // the node's 2px border + half the rendered 8px dot
                                    // (6px forced width + the default 1px ring) = -21px.
                                    '--flow-branch-offset': '-21px',
                                }}
                            />
                        </Box>
                    ))}
                </VStack>
            </Box>
        </Box>
    );
};

export default MultiStepNode;
